import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { User } from '../types';
import './Login.css';

interface PhoneItem {
  id: number;
  phone_number: string;
  telegram_id: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  const [users, setUsers] = useState<User[]>([]);
  const [phones, setPhones] = useState<PhoneItem[]>([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedPhone, setSelectedPhone] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpValidated, setOtpValidated] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    loadUsers();
    loadPhones();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await authApi.getUsers();
      const data = response.data;
      const list =
        Array.isArray(data)
          ? data
          : Array.isArray((data as any)?.users)
          ? (data as any).users
          : Array.isArray((data as any)?.data)
          ? (data as any).data
          : [];
      setUsers(list);
      if (!Array.isArray(list)) {
        setError('Format des utilisateurs invalide');
      }
    } catch (err) {
      setError('Erreur lors du chargement des utilisateurs');
    }
  };

  const loadPhones = async () => {
    try {
      const response = await authApi.getPhones();
      const data = response.data;
      const list =
        Array.isArray(data)
          ? data
          : Array.isArray((data as any)?.phones)
          ? (data as any).phones
          : Array.isArray((data as any)?.data)
          ? (data as any).data
          : [];
      setPhones(list);
      if (Array.isArray(list) && list.length > 0) {
        setSelectedPhone(list[0].phone_number);
      }
    } catch (err) {
      console.error('Erreur chargement téléphones:', err);
    }
  };

  const handleSendOTP = async () => {
    if (!selectedUser || !selectedPhone) {
      setError('Veuillez sélectionner un utilisateur et un numéro de téléphone');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await authApi.sendOTP(selectedUser, selectedPhone);
      setOtpSent(true);
      setSuccess('Code OTP envoyé sur Telegram');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de l\'envoi du code OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUser || !password) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (!otpCode) {
      setError('Veuillez entrer le code OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authApi.login(selectedUser, password, otpCode);

      if (response.data.success) {
        login(response.data.token, {
          id: response.data.user_id,
          user_name: response.data.user_name,
          is_active: true,
          role: response.data.role,
          permissions: response.data.permissions,
        });
        navigate('/');
      } else {
        setError(response.data.message);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>Winalco Contrats et Licenses</h1>
        <h2>Connexion</h2>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label>Utilisateur</label>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              required
            >
              <option value="">Sélectionner un utilisateur</option>
              {users.map((user) => (
                <option key={user.id} value={user.user_name}>
                  {user.user_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          <div className="form-group">
            <label>Téléphone (pour OTP)</label>
            <select
              value={selectedPhone}
              onChange={(e) => setSelectedPhone(e.target.value)}
            >
              <option value="">Sélectionner un téléphone</option>
              {phones.map((phone) => (
                <option key={phone.id} value={phone.phone_number}>
                  {phone.phone_number}
                </option>
              ))}
            </select>
          </div>

          {!otpSent ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleSendOTP}
              disabled={loading || !selectedUser || !selectedPhone}
            >
              {loading ? 'Envoi...' : 'Envoyer code OTP'}
            </button>
          ) : (
            <div className="form-group">
              <label>Code OTP (reçu sur Telegram)</label>
              <input
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                placeholder="Entrez le code à 4 chiffres"
                maxLength={4}
                autoComplete="one-time-code"
              />
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !otpSent || !otpCode}
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>

          {otpSent && (
            <button
              type="button"
              className="btn btn-link"
              onClick={() => {
                setOtpSent(false);
                setOtpCode('');
                setSuccess('');
              }}
            >
              Renvoyer le code OTP
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
